import { useQuery } from "@tanstack/react-query";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import Button from "@mui/material/Button";
import { Link as RouterLink } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { PageHeader } from "@/components/common/PageHeader";
import { KpiCard } from "@/components/common/KpiCard";
import { QueryState } from "@/components/common/AsyncState";
import { useAuth } from "@/auth/useAuth";
import { fetchDashboard } from "@/api/dashboard";
import { fetchCashReconciliations } from "@/api/finance";
import Alert from "@mui/material/Alert";
import { useBranch } from "@/auth/BranchContext";
import { LEAD_STAGE_LABELS, PAYMENT_METHOD_LABELS } from "@/types/enums";
import { formatMoney, formatNumber, formatPercent, periodLabel } from "@/utils/format";

const PIE_COLORS = ["#C8253F", "#1F6FB2", "#C77A0A", "#12876B", "#5C5158"];

export function StaffDashboard() {
  const { hasPermission } = useAuth();
  const { effectiveBranchId } = useBranch();
  const showFinance = hasPermission("dash.finance");
  const showDebts = hasPermission("fin.debts");
  const showReconcile = hasPermission("fin.reconcile");

  const { data, isLoading, error } = useQuery({
    queryKey: ["dashboard", effectiveBranchId],
    queryFn: () => fetchDashboard(effectiveBranchId),
  });

  const { data: openReconciliations = [] } = useQuery({
    queryKey: ["cash-reconciliation", effectiveBranchId, "OPEN"],
    queryFn: () => fetchCashReconciliations({ status: "OPEN", branchId: effectiveBranchId || undefined }),
    enabled: showReconcile,
  });

  return (
    <>
      <PageHeader title="Дашборд" subtitle="Сводка по учебному центру" />
      <QueryState isLoading={isLoading} error={error}>
        {data && (
          <Stack spacing={3}>
            {showReconcile && openReconciliations.length > 0 && (
              <Alert
                severity="error"
                sx={{ border: "1px solid", borderColor: "error.main", bgcolor: "rgba(200, 37, 63, 0.06)" }}
                action={
                  <Button size="small" component={RouterLink} to="/finance">
                    Разобрать →
                  </Button>
                }
              >
                <Typography fontWeight={700} color="error.main">
                  Касса не сходится — {openReconciliations.length} расхождение(й)
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  {openReconciliations
                    .map((row) => {
                      const method = PAYMENT_METHOD_LABELS[row.method as keyof typeof PAYMENT_METHOD_LABELS] ?? row.method;
                      const sign = row.diff > 0 ? "+" : "";
                      return `${row.branchName}: ${method} ${sign}${formatMoney(row.diff)} за ${periodLabel(row.period)}`;
                    })
                    .join(" · ")}
                </Typography>
              </Alert>
            )}
            <Grid container spacing={2}>
              {showFinance && (
                <Grid item xs={12} sm={6} md={3}>
                  <KpiCard
                    label="Приход за месяц"
                    value={formatMoney(data.kpis.monthRevenue)}
                    delta={`${data.kpis.monthRevenueDeltaPercent >= 0 ? "+" : ""}${data.kpis.monthRevenueDeltaPercent}%`}
                    hint="к прошлому месяцу"
                  />
                </Grid>
              )}
              {showFinance && (
                <Grid item xs={12} sm={6} md={3}>
                  <KpiCard
                    label="Задолженность"
                    value={formatMoney(data.kpis.totalDebt)}
                    hint={`${data.kpis.debtorsCount} должников`}
                    color="error.main"
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={3}>
                <KpiCard
                  label="Активные ученики"
                  value={formatNumber(data.kpis.activeStudents)}
                  hint={`+${data.kpis.newStudents} новых / −${data.kpis.leftStudents} ушло`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KpiCard
                  label="Заявки за месяц"
                  value={formatNumber(data.kpis.monthLeads)}
                  hint={`конверсия в оплату ${formatPercent(data.kpis.leadsConversionPercent)}`}
                />
              </Grid>
            </Grid>

            {showFinance && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <Paper variant="outlined" sx={{ p: 2.5 }}>
                    <Typography variant="subtitle1" fontWeight={600} mb={2}>
                      Приход по месяцам
                    </Typography>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={data.revenueByMonth.map((r) => ({ ...r, label: periodLabel(r.period) }))}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="label" />
                        <YAxis tickFormatter={(v) => formatNumber(v)} />
                        <Tooltip formatter={(v: number) => formatMoney(v)} />
                        <Legend />
                        <Bar dataKey="revenue" name="Приход" fill="#C8253F" radius={[6, 6, 0, 0]} />
                        <Bar dataKey="plan" name="План" fill="#D5CBCF" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper variant="outlined" sx={{ p: 2.5, height: "100%" }}>
                    <Typography variant="subtitle1" fontWeight={600} mb={2}>
                      Чем платят
                    </Typography>
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={data.revenueByMethod.map((m) => ({
                            name: PAYMENT_METHOD_LABELS[m.method as keyof typeof PAYMENT_METHOD_LABELS] ?? m.method,
                            value: m.amount,
                          }))}
                          dataKey="value"
                          nameKey="name"
                          outerRadius={90}
                          label={(entry) => entry.name}
                        >
                          {data.revenueByMethod.map((_, index) => (
                            <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v: number) => formatMoney(v)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>
              </Grid>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2.5, height: "100%" }}>
                  <Typography variant="subtitle1" fontWeight={600} mb={1}>
                    Уроки сегодня
                  </Typography>
                  <List dense>
                    {data.todayLessons.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        На сегодня занятий не запланировано.
                      </Typography>
                    )}
                    {data.todayLessons.map((lesson) => (
                      <ListItem
                        key={lesson.id}
                        disableGutters
                        secondaryAction={
                          <Chip
                            size="small"
                            label={lesson.marked ? "Отмечено" : "Не отмечено"}
                            color={lesson.marked ? "success" : "default"}
                            variant={lesson.marked ? "filled" : "outlined"}
                          />
                        }
                      >
                        <ListItemText
                          primary={`${lesson.timeSlot} · ${lesson.groupCode} · ${lesson.subjectName}`}
                          secondary={`${lesson.roomName} · ${lesson.teacherName} · ${lesson.studentsCount} учеников`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2.5, height: "100%" }}>
                  {showDebts ? (
                    <>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          Крупные должники
                        </Typography>
                        <Button size="small" component={RouterLink} to="/finance">
                          Все должники
                        </Button>
                      </Stack>
                      <List dense>
                        {data.topDebtors.length === 0 && (
                          <Typography variant="body2" color="text.secondary">
                            Крупных должников нет.
                          </Typography>
                        )}
                        {data.topDebtors.map((debtor) => (
                          <ListItem
                            key={debtor.studentId}
                            disableGutters
                            secondaryAction={debtor.overdueDays > 0 ? <Chip size="small" color="error" label={`просрочка ${debtor.overdueDays} дн`} /> : undefined}
                          >
                            <ListItemText
                              primary={
                                <Link component={RouterLink} to={`/students/${debtor.studentId}`} underline="hover">
                                  {debtor.studentName}
                                </Link>
                              }
                              secondary={formatMoney(debtor.debt)}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </>
                  ) : (
                    <>
                      <Typography variant="subtitle1" fontWeight={600} mb={1}>
                        Движение учеников
                      </Typography>
                      <Stack direction="row" spacing={4}>
                        <Stack>
                          <Typography variant="h5" color="success.main" fontWeight={700}>
                            +{data.studentMovement.newStudents}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            новых за месяц
                          </Typography>
                        </Stack>
                        <Stack>
                          <Typography variant="h5" color="error.main" fontWeight={700}>
                            −{data.studentMovement.leftStudents}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ушло за месяц
                          </Typography>
                        </Stack>
                      </Stack>
                    </>
                  )}
                </Paper>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <Paper variant="outlined" sx={{ p: 2.5 }}>
                  <Typography variant="subtitle1" fontWeight={600} mb={2}>
                    Посещаемость по месяцам
                  </Typography>
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={data.attendanceByMonth.map((a) => ({ ...a, label: periodLabel(a.period) }))}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" />
                      <YAxis unit="%" domain={[0, 100]} />
                      <Tooltip formatter={(v: number) => `${v}%`} />
                      <Line type="monotone" dataKey="percent" name="Посещаемость" stroke="#12876B" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
              <Grid item xs={12} md={5}>
                <Paper variant="outlined" sx={{ p: 2.5, height: "100%" }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Воронка заявок
                    </Typography>
                    <Button size="small" component={RouterLink} to="/leads">
                      Открыть заявки
                    </Button>
                  </Stack>
                  <Stack spacing={1}>
                    {data.leadsFunnel.map((stage) => (
                      <Stack key={stage.stage} direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2">{LEAD_STAGE_LABELS[stage.stage]}</Typography>
                        <Chip size="small" label={stage.count} />
                      </Stack>
                    ))}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </Stack>
        )}
      </QueryState>
    </>
  );
}
